export enum DashboardWidgetId {
  TotalMembers = "totalMembers",
  ActiveMembers = "activeMembers",
  NewMembers = "newMembers",
  Visitors = "visitors",
  TotalIncome = "totalIncome",
  TotalExpenses = "totalExpenses",
  NetBalance = "netBalance",
  TransfersOut = "transfersOut",
  CashOnHand = "cashOnHand",
  PendingApprovals = "pendingApprovals",
  ActiveActivities = "activeActivities",
  TransactionCount = "transactionCount",
  FinanceTrend = "financeTrend",
  MemberMix = "memberMix",
  MemberGrowth = "memberGrowth",
  ParishGenderInsights = "parishGenderInsights",
  ParishAgeInsights = "parishAgeInsights",
  ParishBranchDistribution = "parishBranchDistribution",
  RecentRecords = "recentRecords",
  ScopeNote = "scopeNote",
  // The D4 branch home tiles. Own ids, never the KPI ids above: `newMembers` already names the KPI tile.
  HomeTreasury = "homeTreasury",
  HomeApprovals = "homeApprovals",
  HomeMembers = "homeMembers",
  HomeRecentVisitors = "homeRecentVisitors",
  HomeBirthdays = "homeBirthdays",
  HomeNewMembers = "homeNewMembers",
  HomeBudget = "homeBudget",
  HomeSpending = "homeSpending",
  /** Who we are (gender and age) on the branch home: the members board mix tile, scoped to the branch. */
  HomeMix = "homeMix",
  // The parish home tiles (D4 style), drawn by the members board widgets with the parish context.
  ParishMembers = "parishMembers",
  ParishBranches = "parishBranches",
  ParishNewMembers = "parishNewMembers",
  ParishVisitors = "parishVisitors",
  ParishToConfirm = "parishToConfirm",
  ParishMix = "parishMix",
  ParishIntegration = "parishIntegration",
}

/** Which board is being built: the same registry serves three surfaces with different defaults. */
export enum DashboardBoardVariant {
  /** The branch home (desktop): the D4 tiles by default, the KPI widgets available under "Add widgets". */
  BranchHome = "branchHome",
  /** The Branch Details console: the legacy branch board, read-only context, unchanged. */
  BranchDetails = "branchDetails",
  Parish = "parish",
}

/**
 * The saved-layout key of each board. The branch home is VERSIONED: layouts saved before D4 live under
 * `admin-overview:branch` and name only the old KPI widgets, so reconciling one would keep those visible and
 * push the D4 tiles to the top of an unrelated arrangement. A fresh key gives every existing user the D4
 * default; the old widgets are still one click away in "Add widgets", and the old key is left untouched for the
 * Branch Details console, which still uses it.
 */
export const DASHBOARD_LAYOUT_KEYS: Record<DashboardBoardVariant, string> = {
  [DashboardBoardVariant.BranchHome]: "admin-overview:branch-home:d4-v1",
  [DashboardBoardVariant.BranchDetails]: "admin-overview:branch",
  // Versioned for the same reason as the branch home: a pre-D4 parish layout names only the KPI widgets.
  [DashboardBoardVariant.Parish]: "admin-overview:parish:d4-v1",
}

/**
 * The parish home default (D4): the church's members beside members per branch (two tracks), then who is new,
 * who visited and who waits for confirmation, then who we are (two tracks) and who still needs a group.
 */
export const PARISH_HOME_D4_WIDGET_SEEDS: readonly DashboardWidgetId[] = [
  DashboardWidgetId.ParishMembers,
  DashboardWidgetId.ParishBranches,
  DashboardWidgetId.ParishNewMembers,
  DashboardWidgetId.ParishVisitors,
  DashboardWidgetId.ParishToConfirm,
  DashboardWidgetId.ParishMix,
  DashboardWidgetId.ParishIntegration,
]

/**
 * The D4 default arrangement: hero row (treasury wide + to validate), two rows of three, then who we are (two
 * tracks wide, as on the parish home).
 */
export const BRANCH_HOME_D4_WIDGET_SEEDS: readonly DashboardWidgetId[] = [
  DashboardWidgetId.HomeTreasury,
  DashboardWidgetId.HomeApprovals,
  DashboardWidgetId.HomeMembers,
  DashboardWidgetId.HomeRecentVisitors,
  DashboardWidgetId.HomeBirthdays,
  DashboardWidgetId.HomeNewMembers,
  DashboardWidgetId.HomeBudget,
  DashboardWidgetId.HomeSpending,
  DashboardWidgetId.HomeMix,
]

export const BRANCH_DASHBOARD_WIDGET_SEEDS: readonly DashboardWidgetId[] = [
  // Balance first: a period figure with nothing to scale it against is what made a -114 month read as a
  // branch in trouble on a branch holding 14,016.
  DashboardWidgetId.CashOnHand,
  DashboardWidgetId.NetBalance,
  DashboardWidgetId.TotalIncome,
  DashboardWidgetId.TotalExpenses,
  DashboardWidgetId.TransfersOut,
  DashboardWidgetId.TotalMembers,
  DashboardWidgetId.ActiveMembers,
  DashboardWidgetId.NewMembers,
  DashboardWidgetId.Visitors,
  DashboardWidgetId.PendingApprovals,
  DashboardWidgetId.ActiveActivities,
  DashboardWidgetId.TransactionCount,
  DashboardWidgetId.FinanceTrend,
  DashboardWidgetId.MemberMix,
  DashboardWidgetId.MemberGrowth,
  DashboardWidgetId.RecentRecords,
  DashboardWidgetId.ScopeNote,
]

export const PARISH_DASHBOARD_WIDGET_SEEDS: readonly DashboardWidgetId[] = [
  DashboardWidgetId.TotalMembers,
  DashboardWidgetId.ActiveMembers,
  DashboardWidgetId.NewMembers,
  DashboardWidgetId.Visitors,
  DashboardWidgetId.PendingApprovals,
  DashboardWidgetId.ParishGenderInsights,
  DashboardWidgetId.ParishAgeInsights,
  DashboardWidgetId.ParishBranchDistribution,
  DashboardWidgetId.MemberGrowth,
  DashboardWidgetId.MemberMix,
  DashboardWidgetId.RecentRecords,
  DashboardWidgetId.ScopeNote,
]
